import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorApi } from '../../lib/contractorApi';

interface Lead {
  id: string;
  lead_ref: string;
  trade: string;
  urgency: string;
  status: string;
  credit_cost: number;
  expires_at: string;
  created_at: string;
  lead_score?: number;
  lead_tier?: 'hot' | 'warm' | 'standard';
  lead_factors?: string[];
  home?: {
    city: string;
    state: string;
    zip_code: string;
    year_built: number | null;
    sq_footage: number | null;
  };
  task?: {
    title: string;
    description: string | null;
    urgency: string;
  };
}

const TIER_CONFIG = {
  hot:      { label: 'Hot lead', bg: '#fef2f2', text: '#dc2626', border: '#fecaca', dot: '#dc2626' },
  warm:     { label: 'Warm lead', bg: '#fffbeb', text: '#92400e', border: '#fde68a', dot: '#f59e0b' },
  standard: { label: 'Standard lead', bg: '#f9fafb', text: '#374151', border: '#e5e7eb', dot: '#9ca3af' },
};

function LeadScoreBadge({ tier, score, factors }: { tier: 'hot' | 'warm' | 'standard'; score: number; factors: string[] }) {
  const cfg = TIER_CONFIG[tier];
  return (
    <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: '8px 12px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: factors.length > 0 ? 4 : 0 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, display: 'inline-block', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: cfg.text }}>{cfg.label}</span>
        <span style={{ fontSize: 12, color: cfg.text, opacity: 0.7, marginLeft: 'auto' }}>{score}/100</span>
      </div>
      {factors.length > 0 && (
        <p style={{ margin: 0, fontSize: 11, color: cfg.text, opacity: 0.85, lineHeight: 1.4 }}>
          {factors.slice(0, 3).join(' · ')}
        </p>
      )}
    </div>
  );
}

interface LeadsResponse {
  data: Lead[];
}

type StatusFilter = 'new' | 'accepted' | 'passed' | 'expired';

const TRADE_LABELS: Record<string, string> = {
  hvac: 'HVAC', plumbing: 'Plumbing', electrical: 'Electrical',
  roofing: 'Roofing', general: 'General Contractor',
  pest_control: 'Pest Control', landscaping: 'Landscaping',
  painting: 'Painting', appliance_repair: 'Appliance Repair', flooring: 'Flooring',
};

const URGENCY_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: '#fef2f2', text: '#dc2626' },
  high:     { bg: '#fffbeb', text: '#d97706' },
  medium:   { bg: '#eff6ff', text: '#2563eb' },
  low:      { bg: '#f0fdf4', text: '#16a34a' },
};

function timeLeft(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const hrs = Math.floor(ms / 3600000);
  if (hrs < 24) return `${hrs}h left`;
  return `${Math.floor(hrs / 24)}d left`;
}

const TABS: { key: StatusFilter; label: string }[] = [
  { key: 'new',      label: 'New' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'passed',   label: 'Passed' },
  { key: 'expired',  label: 'Expired' },
];

export function LeadInbox() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<StatusFilter>('new');
  const [actionError, setActionError] = useState<string | null>(null);
  const [passingId, setPassingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contractor-leads', activeTab],
    queryFn: () => contractorApi.get<LeadsResponse>(`/contractor/leads?status=${activeTab}&limit=50`),
  });

  const acceptMutation = useMutation({
    mutationFn: (leadId: string) => contractorApi.post(`/contractor/leads/${leadId}/accept`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contractor-leads'] });
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Failed to accept lead.');
    },
  });

  const passMutation = useMutation({
    mutationFn: (leadId: string) => contractorApi.post(`/contractor/leads/${leadId}/pass`, { reason: 'not_available' }),
    onSuccess: () => {
      setPassingId(null);
      void queryClient.invalidateQueries({ queryKey: ['contractor-leads'] });
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Failed to pass lead.');
    },
  });

  const leads = data?.data ?? [];

  return (
    <>
      <style>{`
        @media (min-width: 768px) {
          .li-layout { padding-left: 256px !important; }
          .li-content { max-width: 960px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        }
      `}</style>
    <div className="li-layout" style={{ minHeight: '100dvh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#1D4ED8', padding: '48px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => navigate('/app/contractor/dashboard')}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          &#8592;
        </button>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Lead Inbox</h1>
      </div>

      {/* Filter tabs */}
      <div style={{ background: '#1D4ED8', padding: '12px 16px 0', display: 'flex', gap: 4 }}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            style={{
              padding: '8px 16px', border: 'none', cursor: 'pointer',
              borderRadius: '10px 10px 0 0', fontWeight: 600, fontSize: 13,
              background: activeTab === key ? '#f9fafb' : 'rgba(255,255,255,0.15)',
              color: activeTab === key ? '#1D4ED8' : 'rgba(255,255,255,0.85)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px 16px 100px' }}>
        <div className="li-content" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {actionError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 14px' }}>
            <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{actionError}</p>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div style={{
              width: 28, height: 28, border: '3px solid #1D4ED8', borderTopColor: 'transparent',
              borderRadius: '50%', animation: 'spin 0.7s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : leads.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <p style={{ margin: 0, fontSize: 14, color: '#9ca3af' }}>
              No {activeTab} leads.
            </p>
          </div>
        ) : (
          leads.map(lead => {
            const urgencyStyle = URGENCY_COLORS[lead.urgency] ?? { bg: '#f9fafb', text: '#6b7280' };
            const expiry = timeLeft(lead.expires_at);
            const isExpiringSoon = lead.status === 'new' && new Date(lead.expires_at).getTime() - Date.now() < 6 * 3600000;

            return (
              <div
                key={lead.id}
                style={{
                  background: '#fff', borderRadius: 16, padding: 16,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  border: isExpiringSoon ? '1.5px solid #fca5a5' : '1.5px solid transparent',
                }}
              >
                {/* Lead header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#111827' }}>
                      {TRADE_LABELS[lead.trade] ?? lead.trade}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                      {lead.lead_ref}
                      {lead.home ? ` · ${lead.home.city}, ${lead.home.state}` : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{
                      padding: '3px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                      background: urgencyStyle.bg, color: urgencyStyle.text,
                      textTransform: 'capitalize',
                    }}>
                      {lead.urgency}
                    </span>
                    {lead.status === 'new' && (
                      <span style={{ fontSize: 11, color: isExpiringSoon ? '#dc2626' : '#6b7280', fontWeight: isExpiringSoon ? 600 : 400 }}>
                        {expiry}
                      </span>
                    )}
                  </div>
                </div>

                {/* Lead quality score badge */}
                {lead.lead_tier && (
                  <LeadScoreBadge
                    tier={lead.lead_tier}
                    score={lead.lead_score ?? 50}
                    factors={lead.lead_factors ?? []}
                  />
                )}

                {/* Task info */}
                {lead.task && (
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#374151', lineHeight: 1.45 }}>
                    {lead.task.title}
                    {lead.task.description ? ` — ${lead.task.description.slice(0, 80)}${lead.task.description.length > 80 ? '…' : ''}` : ''}
                  </p>
                )}

                {/* Home details */}
                {lead.home && (lead.home.sq_footage ?? lead.home.year_built) && (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    {lead.home.sq_footage && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        {lead.home.sq_footage.toLocaleString()} sq ft
                      </span>
                    )}
                    {lead.home.year_built && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Built {lead.home.year_built}
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: '#6b7280' }}>ZIP {lead.home.zip_code}</span>
                  </div>
                )}

                {/* Credit cost */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: '#8b5cf6',
                    background: '#f5f3ff', padding: '3px 8px', borderRadius: 99,
                  }}>
                    {lead.credit_cost} credit{lead.credit_cost !== 1 ? 's' : ''}
                  </span>

                  {lead.status === 'new' && passingId !== lead.id && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setPassingId(lead.id)}
                        disabled={passMutation.isPending || acceptMutation.isPending}
                        style={{
                          padding: '7px 14px', border: '1.5px solid #e5e7eb', background: '#fff',
                          borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#6b7280',
                        }}
                      >
                        Pass
                      </button>
                      <button
                        type="button"
                        onClick={() => acceptMutation.mutate(lead.id)}
                        disabled={acceptMutation.isPending || passMutation.isPending}
                        style={{
                          padding: '7px 16px', background: '#1D4ED8', border: 'none',
                          borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff',
                        }}
                      >
                        {acceptMutation.isPending ? 'Accepting…' : 'Accept'}
                      </button>
                    </div>
                  )}

                  {passingId === lead.id && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: '#6b7280' }}>Pass this lead?</span>
                      <button
                        type="button"
                        onClick={() => setPassingId(null)}
                        style={{ padding: '6px 10px', border: '1.5px solid #e5e7eb', background: '#fff', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#6b7280' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => passMutation.mutate(lead.id)}
                        disabled={passMutation.isPending}
                        style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}
                      >
                        {passMutation.isPending ? '…' : 'Confirm'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        </div>
      </div>
    </div>
    </>
  );
}
